import type { User } from '@iheartjane/data-access';
import { Storage } from '@iheartjane/utils';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import cloneDeep from 'lodash-es/cloneDeep';
import type { DeepReadonly } from 'ts-essentials';

import { closeModal, setPreviousPath } from '../../customer/redux/application';
import { NotificationsService } from '../../services/notifications';
import { UsersSource } from '../../sources/users';
import type { CustomerThunkAction } from '../customerAction';
import { customerLogOutSuccess } from './customer';

const USER_SESSION_STORAGE_KEY = 'user_session';

export const updateUsers = ({
  attributes,
  disableNotification = false,
  onSuccess,
  onError,
}: {
  attributes: Record<string, string | boolean | undefined>;
  disableNotification?: boolean;
  onError?: (validations: Record<string, string[]>) => void;
  onSuccess?: () => void;
}): CustomerThunkAction => {
  const updates = cloneDeep(attributes);

  if (updates['phone'] && typeof updates['phone'] === 'string') {
    updates['phone'] = updates['phone'].replace(/-/g, '');
  }

  return (dispatch) => {
    UsersSource.update({
      user: { ...(({ promotions_opt_out: _, ...rest }) => rest)(updates) },
      promotions_opt_out: updates['promotions_opt_out'] as boolean,
    }).then((result) => {
      const { user, validations } = result;

      if (!validations) {
        !disableNotification &&
          NotificationsService.success('Profile has been updated');
        Storage.set(USER_SESSION_STORAGE_KEY, user);
        onSuccess && onSuccess();
        dispatch(updateUsersSuccess(user));
      } else {
        onError && onError(validations);
      }
    });
  };
};

export type UsersActions =
  | ReturnType<typeof setPreviousPath>
  | ReturnType<typeof updateUsersSuccess>;

export type UsersState = DeepReadonly<{
  hasLoadedReservations: boolean;
  isCancellingReservation: boolean;
  isLoading: boolean;
  isLoadingReservations: boolean;
  previousPath: string;
  productsOrdered: number;
  reviewsLeft: number;
  shouldUpdate: boolean;
  user:
    | {
        birth_date: string;
        email: string;
        nickname: string;
        password: string;
        phone: string;
        promotions_opt_out: boolean;
      }
    | User;
}>;

const getInitialState = (): UsersState => ({
  user: {
    birth_date: '',
    nickname: '',
    email: '',
    phone: '',
    password: '',
    promotions_opt_out: false,
  },
  productsOrdered: 0,
  reviewsLeft: 0,
  shouldUpdate: true,
  hasLoadedReservations: false,
  isCancellingReservation: false,
  isLoadingReservations: false,
  isLoading: true,
  previousPath: '',
});

const usersSlice = createSlice({
  name: 'users',
  initialState: getInitialState(),
  reducers: {
    updateUsersSuccess: (state, action: PayloadAction<User>) => {
      return { ...state, user: { ...state.user, ...action.payload } };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setPreviousPath, (state, action) => {
      return { ...state, previousPath: action.payload };
    });
    builder.addCase(closeModal, (state) => {
      return { ...state, user: getInitialState().user };
    });
    builder.addCase(customerLogOutSuccess, (state) => {
      return {
        ...state,
        user: getInitialState().user,
        hasLoadedReservations: false,
      };
    });
  },
  selectors: {
    selectUsers: (state) => state,
  },
});

export const {
  actions: { updateUsersSuccess },
  reducer: usersReducer,
  selectors: { selectUsers },
} = usersSlice;
